import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import dayjs from "dayjs";
import { Subject, Subscription } from "rxjs";
import { CalendarEvent, CalendarView } from "angular-calendar";
import { BrandLinkedInPostLog } from "../../../shared/models/brandLinkedInPostLog";
import { PostPlatform } from "../../../shared/enums/campaignPost.enums";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { DateService } from "../../../shared/services/date.service";
import { BrandPostLog } from "../../../shared/types/postLog.types";
import { BrandCampaignCalendarPost } from "../../../shared/models/brandCampaignCalendarPost";
import { BrandFacebookPostLog } from "../../../shared/models/brandFacebookPostLog";
import { BrandGoogleAdPostLog } from "../../../shared/models/brandGoogleAdPostLog";
import { DialogService } from "../../../shared/services/dialog.service";
import {
  BrandCampaignPostLogService,
  PostLogsFilters,
} from "../shared/services/brand-campaign-post-log.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { getLanguageSignal } from "../../../shared/services/language.service";

// TODO: This should use `CalendarEvent.meta` instead of extending it
interface BrandCalendarEvent extends CalendarEvent {
  blocked: boolean;
  editable: boolean;
  isSuggestedPost: boolean;
  post: BrandPostLog | BrandCampaignCalendarPost;
}

@Component({
  selector: "app-brand-campaign-posts-calendar",
  templateUrl: "./brand-campaign-posts-calendar.component.html",
  styleUrl: "./brand-campaign-posts-calendar.component.scss",
})
export class BrandCampaignPostsCalendarComponent implements OnInit, OnDestroy {
  protected readonly language = getLanguageSignal();
  protected readonly PostPlatform = PostPlatform;
  protected loading = true;

  // Calendar specific variables
  protected events: BrandCalendarEvent[] = [];
  protected refresh = new Subject<any>();
  protected view: CalendarView | "month" | "week" | "day" = "month";
  protected viewDate = new Date();

  private readonly limit = 100;
  private readonly subscriptions = new Subscription();
  private campaign: BrandCampaign;
  private campaignEndDate = new Date();
  private campaignStartDate = new Date();
  private offset = 0;
  private postLogs: BrandPostLog[] = [];

  constructor(
    private readonly dialogService: DialogService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly brandCampaignPostLogService: BrandCampaignPostLogService,
    private readonly dateService: DateService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
    this.campaignStartDate = dayjs(this.campaign.startDate)
      .startOf("day")
      .toDate();
    this.campaignEndDate = dayjs(this.campaign.endDate).endOf("day").toDate();
    this.viewDate = this.dateService.min(this.campaignEndDate, new Date());
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.loadCalendarPosts();
  }

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  protected calendarDateChanged(): void {
    this.postLogs = [];
    this.offset = 0;
    this.loadCalendarPosts();
  }

  private loadCalendarPosts(): void {
    this.loading = true;
    const startDate = dayjs(this.viewDate).startOf("month");
    startDate.subtract(7, "days");
    const endDate = dayjs(this.viewDate).endOf("month");
    endDate.add(7, "days");

    const filters: Partial<PostLogsFilters> = {
      facebookPosts: true,
      googleAds: true,
      linkedinPosts: true,
      startTime: startDate.unix(),
      endTime: endDate.unix(),
    };

    this.subscriptions.add(
      this.brandCampaignPostLogService
        .getBrandCampaignPostLogs(
          this.brandCampaignService.currentBrandCampaignId,
          filters,
          this.offset,
          this.limit,
        )
        .subscribe((list) => {
          if (list.items.length > 0 && list.totalCount > this.postLogs.length) {
            this.offset = this.offset + this.limit;
            this.postLogs = this.postLogs.concat(list.items);
            this.loadCalendarPosts();
          } else {
            this.loading = false;
            this.initEvents();
          }
        }),
    );
  }

  private initEvents(): void {
    this.events = [];

    if (this.campaign && this.postLogs) {
      this.postLogs.forEach((post) => this.processPost(post));

      if (
        this.campaign.currentDetails &&
        this.campaign.currentDetails.calendars
      ) {
        this.campaign.currentDetails.calendars.forEach((calendar) => {
          calendar.calendarPosts.forEach((calendarPost) =>
            this.processPost(calendarPost),
          );
        });
      }
    }
  }

  private getPostTitle(post: BrandPostLog | BrandCampaignCalendarPost): string {
    if (post.isFacebookPost) {
      return (post as BrandFacebookPostLog).text;
    } else if (post.isGoogleAd) {
      return (post as BrandGoogleAdPostLog).descriptionOne;
    } else if (post.isLinkedInPost) {
      return (post as BrandLinkedInPostLog).text;
    }

    return "";
  }

  private processPost(post: BrandPostLog | BrandCampaignCalendarPost): void {
    this.events.push({
      title: this.getPostTitle(post),
      start: this.getPostDate(post),
      post: post,
      isSuggestedPost: post instanceof BrandCampaignCalendarPost,
      editable: false,
      draggable: false,
      blocked: false,
    });
  }

  private getPostDate(post: BrandPostLog | BrandCampaignCalendarPost): Date {
    if (post instanceof BrandCampaignCalendarPost) {
      return post.suggestedPublishDate;
    }

    return post.scheduledPublishDate ? post.scheduledPublishDate : post.date;
  }

  protected onEventClick(
    mouseEvent: MouseEvent,
    event: BrandCalendarEvent,
  ): void {
    if (mouseEvent.stopPropagation) {
      mouseEvent.stopPropagation();
    }

    this.dialogService.showPostPreview({ post: event.post });
  }

  protected isCalendarOnThisMonth(): boolean {
    const now = new Date();

    return (
      now.getMonth() === this.viewDate.getMonth() &&
      now.getFullYear() === this.viewDate.getFullYear()
    );
  }

  protected isDayOutsideRange(date: Date): boolean {
    return this.dateService.isDateOutsideRange(date, {
      startDate: this.campaignStartDate,
      endDate: this.campaignEndDate,
    });
  }
}
